import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin, Observable } from 'rxjs';
import { FormFieldComponent } from '../../../../../../shared/components/form-field/form-field.component';
import { AzButtonComponent } from '../../../../../../shared/components/az-button/az-button.component';
import { StatusChangeModalComponent } from '../../../../../../shared/components/status-change-modal/status-change-modal.component';
import { LegalEntityService } from '../../../../services/legal-entity.service';
import { ContactInformationDto } from '../../../../models/legal-entity.models';
import { UserPermissionsService } from '../../../../../../core/auth/services/user-permissions.service';
import { DocumentRepositoryService } from '../../../../../document-repository/services/document-repository.service';

interface ContactRow {
  id: number;
  contactName: string;
  contactEmail: string;
  phone: string;
  createdDate: number;
  lastUpdatedDate: number | null;
  isEditing: boolean;
  isSaving: boolean;
  originalContactName: string;
  originalContactEmail: string;
  originalPhone: string;
  currentContactName: string;
  currentContactEmail: string;
  currentPhone: string;
}

@Component({
  selector: 'app-contact-information',
  standalone: true,
  imports: [CommonModule, FormFieldComponent, AzButtonComponent, StatusChangeModalComponent],
  templateUrl: './contact-information.component.html',
  styleUrls: ['./contact-information.component.scss'],
})
export class ContactInformationComponent implements OnChanges {
  @Input() legalEntityId: number | null = null;
  @Input() set contacts(value: ContactInformationDto[]) {
    if (value && value.length > 0) {
      this.contactRows = value.map((contact) => ({
        id: contact.id,
        contactName: contact.contact_name || '',
        contactEmail: contact.contact_email,
        phone: contact.phone || '',
        createdDate: contact.created_date,
        lastUpdatedDate: contact.last_updated_date,
        isEditing: false,
        isSaving: false,
        originalContactName: contact.contact_name || '',
        originalContactEmail: contact.contact_email,
        originalPhone: contact.phone || '',
        currentContactName: contact.contact_name || '',
        currentContactEmail: contact.contact_email,
        currentPhone: contact.phone || '',
      }));
    } else {
      // No contacts — create one empty placeholder row so the user can add the first contact
      this.contactRows = [
        {
          id: 0,
          contactName: '',
          contactEmail: '',
          phone: '',
          createdDate: 0,
          lastUpdatedDate: null,
          isEditing: false,
          isSaving: false,
          originalContactName: '',
          originalContactEmail: '',
          originalPhone: '',
          currentContactName: '',
          currentContactEmail: '',
          currentPhone: '',
        },
      ];
    }
  }

  contactRows: ContactRow[] = [];

  @Output() contactsChanged = new EventEmitter<ContactInformationDto[]>();

  showContactSuccessModal = false;
  showContactProcessingModal = false;
  showContactErrorModal = false;
  showComplianceWarningModal = false;
  showPendingDocsWarningModal = false;
  contactProcessingTitle = 'Adding Contact';
  contactSuccessTitle = 'Contact Added';
  contactSuccessMessage = '';
  contactErrorMessage = '';
  isAddContactSuccess = false;
  private pendingAction: 'addContact' | 'editContact' | null = null;

  // New contact being added
  isAddingContact = false;
  newContactName = '';
  newContactEmail = '';
  newContactPhone = '';
  isSavingNewContact = false;

  constructor(
    private legalEntityService: LegalEntityService,
    public userPermissions: UserPermissionsService,
    private documentRepositoryService: DocumentRepositoryService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Reset edit/add mode when legalEntityId changes (user selected a different entity)
    if (changes['legalEntityId'] && !changes['legalEntityId'].firstChange) {
      this.isAddingContact = false;
      this.newContactName = '';
      this.newContactEmail = '';
      this.newContactPhone = '';
      this.isSavingNewContact = false;
      // Reset all contacts to non-editing state
      this.contactRows.forEach((contact) => {
        contact.isEditing = false;
        contact.isSaving = false;
      });
    }
  }

  get canEdit(): boolean {
    return this.userPermissions.hasEditContactInformationPermission();
  }

  hasChanges(contact: ContactRow): boolean {
    return (
      contact.currentContactName !== contact.originalContactName ||
      contact.currentContactEmail !== contact.originalContactEmail ||
      contact.currentPhone !== contact.originalPhone
    );
  }

  private isValidEmail(email: string): boolean {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailPattern.test(email.trim());
  }

  hasInvalidEmail(contact: ContactRow): boolean {
    const email = contact.isEditing ? contact.currentContactEmail : contact.contactEmail;
    return email.trim() !== '' && !this.isValidEmail(email);
  }

  get hasInvalidNewEmail(): boolean {
    return this.newContactEmail.trim() !== '' && !this.isValidEmail(this.newContactEmail);
  }

  get canSaveNewContact(): boolean {
    // Only email is required, name and phone are optional
    return this.isValidEmail(this.newContactEmail);
  }

  get isAnyContactEditing(): boolean {
    return this.contactRows.some((contact) => contact.isEditing);
  }

  toggleEdit(contact: ContactRow): void {
    if (!contact.isEditing) {
      // Entering edit mode - store original values
      contact.originalContactName = contact.contactName;
      contact.originalContactEmail = contact.contactEmail;
      contact.originalPhone = contact.phone;
      contact.currentContactName = contact.contactName;
      contact.currentContactEmail = contact.contactEmail;
      contact.currentPhone = contact.phone;
    }
    contact.isEditing = !contact.isEditing;
  }

  toggleEditAll(): void {
    // Enable edit mode for all contacts (including empty row when no contacts)
    this.contactRows.forEach((contact) => {
      contact.isEditing = true;
      // Store original values when entering edit mode
      if (!contact.originalContactName) {
        contact.originalContactName = contact.contactName;
        contact.originalContactEmail = contact.contactEmail;
        contact.originalPhone = contact.phone;
        contact.currentContactName = contact.contactName;
        contact.currentContactEmail = contact.contactEmail;
        contact.currentPhone = contact.phone;
      }
    });
  }

  discard(contact: ContactRow): void {
    // Revert changes to original values
    contact.currentContactName = contact.originalContactName;
    contact.currentContactEmail = contact.originalContactEmail;
    contact.currentPhone = contact.originalPhone;
    contact.isEditing = false;
  }

  saveChanges(contact: ContactRow): void {
    if (!contact.id || !this.legalEntityId || contact.isSaving) {
      return;
    }

    contact.isSaving = true;

    this.contactProcessingTitle = 'Saving Changes';
    this.showContactProcessingModal = true;

    const payload = {
      id: contact.id,
      contact_name: contact.currentContactName,
      contact_email: contact.currentContactEmail,
      phone: contact.currentPhone || null,
      legal_entity_id: this.legalEntityId,
      created_date: contact.createdDate,
      last_updated_date: contact.lastUpdatedDate,
    };

    this.legalEntityService.updateContactInformation([payload]).subscribe({
      next: (responses) => {
        const response = responses[0];
        // Update the displayed values with the response
        contact.contactName = response.contact_name || '';
        contact.contactEmail = response.contact_email;
        contact.phone = response.phone || '';
        contact.lastUpdatedDate = response.last_updated_date;

        // Update original values to reflect saved state
        contact.originalContactName = response.contact_name || '';
        contact.originalContactEmail = response.contact_email;
        contact.originalPhone = response.phone || '';

        // Exit edit mode
        contact.isEditing = false;
        contact.isSaving = false;
        this.showContactProcessingModal = false;
        this.contactSuccessTitle = 'Changes Saved';
        this.contactSuccessMessage = 'The contact information has been successfully updated.';
        this.isAddContactSuccess = false;
        this.showContactSuccessModal = true;

        this.refreshContactList();
      },
      error: (err) => {
        contact.isSaving = false;
        this.showContactProcessingModal = false;
        this.contactErrorMessage =
          err?.error?.message || 'An error occurred while saving the contact.';
        this.showContactErrorModal = true;
      },
    });
  }

  saveAllChanges(): void {
    const editingContacts = this.contactRows.filter((contact) => contact.isEditing);

    if (editingContacts.length === 0 || !this.legalEntityId) {
      return;
    }

    editingContacts.forEach((contact) => {
      contact.isSaving = true;
    });

    const newContacts = editingContacts.filter((contact) => contact.id === 0);
    const existingContacts = editingContacts.filter((contact) => contact.id > 0);

    const observables: Observable<unknown>[] = [];

    newContacts.forEach((contact) => {
      observables.push(
        this.legalEntityService.createContactInformation({
          id: 0,
          contact_name: contact.currentContactName,
          contact_email: contact.currentContactEmail,
          phone: contact.currentPhone || null,
          legal_entity_id: this.legalEntityId!,
          created_date: 0,
          last_updated_date: 0,
        }),
      );
    });

    if (existingContacts.length > 0) {
      const payloads = existingContacts.map((contact) => ({
        id: contact.id,
        contact_name: contact.currentContactName,
        contact_email: contact.currentContactEmail,
        phone: contact.currentPhone || null,
        legal_entity_id: this.legalEntityId!,
        created_date: contact.createdDate,
        last_updated_date: contact.lastUpdatedDate,
      }));
      observables.push(this.legalEntityService.updateContactInformation(payloads));
    }

    this.contactProcessingTitle = 'Saving Changes';
    this.showContactProcessingModal = true;

    forkJoin(observables).subscribe({
      next: () => {
        editingContacts.forEach((contact) => {
          contact.isSaving = false;
        });
        this.showContactProcessingModal = false;
        this.refreshContactList();
        this.contactSuccessTitle = 'Changes Saved';
        this.contactSuccessMessage = 'The contact information has been successfully updated.';
        this.isAddContactSuccess = false;
        this.showContactSuccessModal = true;
      },
      error: (err) => {
        editingContacts.forEach((contact) => {
          contact.isSaving = false;
        });
        this.showContactProcessingModal = false;
        this.contactErrorMessage =
          err?.error?.message || 'An error occurred while saving the changes.';
        this.showContactErrorModal = true;
      },
    });
  }

  onContactNameChange(contact: ContactRow, value: string): void {
    contact.currentContactName = value;
  }

  onContactEmailChange(contact: ContactRow, value: string): void {
    contact.currentContactEmail = value;
  }

  onPhoneChange(contact: ContactRow, value: string): void {
    contact.currentPhone = value;
  }

  handleDiscard(): void {
    // Discard new contact
    if (this.isAddingContact) {
      this.discardNewContact();
    }
    // Discard all editing contacts
    this.contactRows.forEach((contact) => {
      if (contact.isEditing) {
        this.discard(contact);
      }
    });
  }

  handleSave(): void {
    this.contactProcessingTitle = 'Checking Documents...';
    this.showContactProcessingModal = true;

    this.documentRepositoryService.getDocuments().subscribe({
      next: (documents) => {
        this.showContactProcessingModal = false;
        const hasPending = documents.some((doc) => doc.status === 'pending');
        if (hasPending) {
          this.showPendingDocsWarningModal = true;
        } else {
          this.executeSave();
        }
      },
      error: () => {
        // If the check fails, proceed with save to avoid blocking the user
        this.showContactProcessingModal = false;
        this.executeSave();
      },
    });
  }

  private executeSave(): void {
    if (this.isAddingContact) {
      this.saveNewContact();
    } else {
      this.saveAllChanges();
    }
  }

  onPendingDocsWarningConfirm(): void {
    this.showPendingDocsWarningModal = false;
    this.executeSave();
  }

  onPendingDocsWarningCancel(): void {
    this.showPendingDocsWarningModal = false;
  }

  getDisableSaveButton(): boolean {
    if (this.isAddingContact) {
      return !this.canSaveNewContact || this.isSavingNewContact;
    } else {
      const editingContacts = this.contactRows.filter((contact) => contact.isEditing);
      if (editingContacts.length > 0) {
        const anySaving = editingContacts.some((contact) => contact.isSaving);
        // Email is required and must be valid for all contacts
        const allHaveValidEmail = editingContacts.every((contact) =>
          this.isValidEmail(contact.currentContactEmail),
        );

        // For new contacts (id: 0), check if email is filled
        // For existing contacts, check if there are changes
        const hasNewContacts = editingContacts.some((contact) => contact.id === 0);
        const hasChangesOrNewContact = hasNewContacts
          ? allHaveValidEmail
          : editingContacts.some((contact) => this.hasChanges(contact));

        return !hasChangesOrNewContact || anySaving || !allHaveValidEmail;
      }
    }
    return true;
  }

  closeComplianceWarningModal(): void {
    const action = this.pendingAction;
    this.pendingAction = null;
    this.showComplianceWarningModal = false;

    if (action === 'addContact') {
      this.isAddingContact = true;
      this.newContactName = '';
      this.newContactEmail = '';
      this.newContactPhone = '';
    } else if (action === 'editContact') {
      this.toggleEditAll();
    }
  }

  requestAddContact(): void {
    this.pendingAction = 'addContact';
    this.showComplianceWarningModal = true;
  }

  requestEditContacts(): void {
    this.pendingAction = 'editContact';
    this.showComplianceWarningModal = true;
  }

  addContact(): void {
    this.isAddingContact = true;
    this.newContactName = '';
    this.newContactEmail = '';
    this.newContactPhone = '';
  }

  discardNewContact(): void {
    this.isAddingContact = false;
    this.newContactName = '';
    this.newContactEmail = '';
    this.newContactPhone = '';
  }

  saveNewContact(): void {
    if (!this.legalEntityId || this.isSavingNewContact || !this.canSaveNewContact) {
      return;
    }

    this.isSavingNewContact = true;
    this.contactProcessingTitle = 'Adding Contact';
    this.showContactProcessingModal = true;

    const payload = {
      id: 0,
      contact_name: this.newContactName,
      contact_email: this.newContactEmail,
      phone: this.newContactPhone || null,
      legal_entity_id: this.legalEntityId,
      created_date: 0,
      last_updated_date: 0,
    };

    this.legalEntityService.createContactInformation(payload).subscribe({
      next: () => {
        // Reset new contact form
        this.isAddingContact = false;
        this.newContactName = '';
        this.newContactEmail = '';
        this.newContactPhone = '';
        this.isSavingNewContact = false;
        this.showContactProcessingModal = false;
        // Refresh contact list from the server
        this.refreshContactList();
        // Show success modal
        this.contactSuccessTitle = 'Contact Added';
        this.contactSuccessMessage = 'The contact has been successfully added to the Legal Entity.';
        this.isAddContactSuccess = true;
        this.showContactSuccessModal = true;
      },
      error: (err) => {
        this.isSavingNewContact = false;
        this.showContactProcessingModal = false;
        this.contactErrorMessage =
          err?.error?.message || 'An error occurred while adding the contact.';
        this.showContactErrorModal = true;
      },
    });
  }

  private refreshContactList(): void {
    if (!this.legalEntityId) {
      return;
    }

    this.legalEntityService.getContactInformation(this.legalEntityId).subscribe({
      next: (contacts) => {
        this.contacts = contacts;
        this.contactsChanged.emit(contacts);
      },
      error: () => {
        // Refresh failed — still emit current state so parent stays in sync
        this.contactsChanged.emit(
          this.contactRows
            .filter((r) => r.id > 0)
            .map((r) => ({
              id: r.id,
              contact_name: r.contactName,
              contact_email: r.contactEmail,
              phone: r.phone || null,
              legal_entity_id: this.legalEntityId!,
              created_date: r.createdDate,
              last_updated_date: r.lastUpdatedDate,
            })),
        );
      },
    });
  }

  onNewContactNameChange(value: string): void {
    this.newContactName = value;
  }

  onNewContactEmailChange(value: string): void {
    this.newContactEmail = value;
  }

  onNewContactPhoneChange(value: string): void {
    this.newContactPhone = value;
  }

  closeContactSuccessModal(): void {
    this.showContactSuccessModal = false;
  }

  closeContactErrorModal(): void {
    this.showContactErrorModal = false;
  }

  closeContactProcessingModal(): void {
    this.showContactProcessingModal = false;
  }
}
